// v4.0 — @handle claim banner. Social identity requires a unique handle;
// this prompts any signed-in user who hasn't claimed one yet (new users on
// first social use, and legacy accounts). Full profile editing lives at
// /profile/ownership — this banner handles the quick claim inline.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = window.__SG_API_BASE__ || process.env.REACT_APP_BACKEND_URL;

export const HandleClaimBanner = () => {
  const [needed, setNeeded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [handle, setHandle] = useState('');
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [saving, setSaving] = useState(false);

  const probe = () => {
    setLoadFailed(false);
    axios.get(`${API}/api/profiles/me`, { withCredentials: true })
      .then(r => setNeeded(!r.data?.handle))
      .catch((e) => {
        // A failed probe must NOT silently suppress the prompt — show a
        // retry state unless the user simply isn't signed in (401).
        if (e?.response?.status === 401) { setNeeded(false); return; }
        setLoadFailed(true);
      });
  };
  useEffect(() => { probe(); }, []);

  useEffect(() => {
    if (!handle || handle.length < 3) { setAvailability(null); return; }
    setChecking(true);
    const t = setTimeout(() => {
      axios.get(`${API}/api/handles/check`, { params: { handle: handle.toLowerCase() }, withCredentials: true })
        .then(r => setAvailability(r.data))
        .catch(() => setAvailability(null))
        .finally(() => setChecking(false));
    }, 350);
    return () => clearTimeout(t);
  }, [handle]);

  if (loadFailed) {
    return (
      <div data-testid="handle-claim-banner-error"
        className="rounded-2xl border border-[#E5E4E2] bg-white p-4 mb-6 flex items-center justify-between gap-3">
        <p className="font-sans text-xs text-[#8E8A84]">We couldn't check your profile identity.</p>
        <button onClick={probe} data-testid="handle-claim-retry"
          className="font-sans text-[11px] uppercase tracking-[0.16em] text-[#0D0D0D] underline">Retry</button>
      </div>
    );
  }
  if (!needed) return null;

  const claim = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/handles/claim`, { handle: handle.trim().toLowerCase() }, { withCredentials: true });
      toast.success(`You're @${handle.trim().toLowerCase()} — welcome to the Style Network.`);
      setNeeded(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim that handle.');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="handle-claim-banner"
      className="rounded-2xl border border-[#C7A96B]/50 bg-[#0D0D0D] text-white p-4 md:p-5 mb-6">
      <p className="font-serif text-lg">Claim your @handle</p>
      <p className="font-sans text-xs text-white/70 mt-1">
        Your handle is your identity on the Style Network — it's how people find, follow and tag you.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C7A96B] font-sans text-sm">@</span>
          <input value={handle} onChange={e => setHandle(e.target.value.replace(/[^a-zA-Z0-9._]/g, ''))}
            placeholder="yourname" maxLength={30} data-testid="handle-claim-input"
            aria-label="Choose your handle"
            className="w-full pl-8 pr-3 py-2.5 rounded-full bg-white/10 border border-white/20 font-sans text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#C7A96B]" />
        </div>
        <button onClick={claim} disabled={saving || checking || !handle || (availability && availability.available === false)}
          data-testid="handle-claim-submit"
          className="px-5 py-2.5 rounded-full bg-[#C7A96B] text-[#0D0D0D] font-sans text-xs font-semibold disabled:opacity-50 min-h-[44px]">
          {saving ? 'Claiming…' : 'Claim handle'}
        </button>
      </div>
      {availability && handle.length >= 3 && (
        <p className={`font-sans text-[11px] mt-2 ${availability.available ? 'text-emerald-300' : 'text-red-300'}`}
          data-testid="handle-availability">
          {availability.available ? `@${handle.toLowerCase()} is available` : (availability.reason || 'That handle is taken.')}
        </p>
      )}
      <Link to="/profile/ownership" className="font-sans text-[11px] text-white/60 underline mt-2 inline-block">
        Full profile settings →
      </Link>
    </div>
  );
};
